import {
    ISimplePresentation,
    ISimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {gsap} from "gsap";
import {KennethMustDie} from "../KennethMustDie";
import {IFeatureInfo, SPECIAL_SYMBOL_NAMES, X_NUDGE_ANIMATIONS} from "../server/IKMDData";
import {KMDSymbol} from "../reels/KMDSymbol";
import {KMDSound} from "../sound/KMDSound";

export class KMDMainReelsXNudgeRunner implements ISimplePresentation {

    public shouldRun(): boolean {
        return (KennethMustDie.model.xNudgeMainReels.length > 0);
    }

    public start(controller: ISimplePresentationRunnerController): void {
        const isWin: boolean = KennethMustDie.model.gameData.betWayWins.length > 0,
            tl: gsap.core.Timeline = gsap.timeline({
                delay: 0.2,
                onComplete: () => this.handleComplete(controller, isWin)
            }),
            multipliers: number[][] = KennethMustDie.model.gameData.multiplier,
            xNudgeData: IFeatureInfo[] = KennethMustDie.model.xNudgeMainReels;

        this.handleStartOfNudge(isWin, controller, tl);

        xNudgeData.forEach((xNudge: IFeatureInfo) => {
            const reelId: number = xNudge.originReelId,
                symId: number = xNudge.originSymId;
            let animName: string,
                nudgeSteps: number = symId,
                xNudgeSymbol: KMDSymbol,
                multipliersNudgeSteps: number[] = [],
                multipliersYIdx: number[] = [];

            xNudgeSymbol = KennethMustDie.reelView.reelModels[reelId].getStopSymbolAt(symId).positionable as KMDSymbol;
            animName = this.getMainReelsAnimName(symId, multipliersNudgeSteps, multipliers[reelId], multipliersYIdx);

            // #418
            tl.add(() => {
                this.handleStickyWildsAndStickyXNudge(reelId, symId);
            });

            if (xNudgeSymbol) {
                xNudgeSymbol.parent.addChild(xNudgeSymbol);
            }

            for (let i: number = 0; i < multipliersNudgeSteps.length; i++) {
                multipliersNudgeSteps[i] = (Math.max(multipliersNudgeSteps[i], 1));
            }

            animName += (isWin ? X_NUDGE_ANIMATIONS.WIN_HELPER : X_NUDGE_ANIMATIONS.NO_WIN_HELPER);

            const nudgeAnim: gsap.core.Timeline = xNudgeSymbol.setState(animName),
                wholeTlDurationTllNow: number = tl.totalDuration();

            this.getNudgeMultiplierAnim(nudgeSteps, tl, nudgeAnim, wholeTlDurationTllNow, multipliersNudgeSteps, isWin, xNudgeSymbol, multipliersYIdx, reelId);
            tl.add(nudgeAnim, wholeTlDurationTllNow);
        });

        if (!isWin) {
            // #370, 200 ms are added even if we timeScale it.
            tl.to(this, {
                duration: 0.2 * 10
            });

            tl.timeScale(10);
        }
    }

    protected handleStartOfNudge(isWin: boolean, controller: ISimplePresentationRunnerController, tl: gsap.core.Timeline): void {
        if (isWin) {
            controller.setSkipCallback(() => {
                this.onSkip(tl);
            });

            tl.add(() => {
                KennethMustDie.xHoleBlackBg.show(0);
                KennethMustDie.xHoleBlackBg.playFlicker();
            }, 0);
        } else {
            // #239
            tl.add(() => {
                KennethMustDie.soundPlayer.playSound(KMDSound.X_NUDGE_NUDGE_FAST);
            }, 0);
        }
    }

    protected handleComplete(controller: ISimplePresentationRunnerController, isWin: boolean): void {
        controller.finish();

        if (isWin) {
            KennethMustDie.xHoleBlackBg.stopFlicker();
            KennethMustDie.xHoleBlackBg.hide();
        }
    }

    protected getNudgeMultiplierAnim(nudgeSteps: number, tl: gsap.core.Timeline, nudgeAnim: gsap.core.Timeline, wholeTlDurationTllNow: number, multipliersNudgeSteps: number[], isWin: boolean, xNudgeSymbol: KMDSymbol, multipliersYIdx: number[], reelId: number): void {
        const eachStepDuration: number = nudgeAnim.totalDuration() / nudgeSteps;

        for (let step: number = 0; step < nudgeSteps; step++) {
            tl.add(() => {
                const sym: KMDSymbol = (KennethMustDie.reelView.reelModels[reelId].getStopSymbolAt((nudgeSteps - step - 1)).positionable as KMDSymbol);
                let multiplier: number = multipliersNudgeSteps[step];

                if (sym.visitedByNudge) {
                    multiplier = 1;
                } else {
                    if (sym.name === SPECIAL_SYMBOL_NAMES.W) {
                        multiplier *= 2;
                    } else if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
                        multiplier = sym.xNudgeSymbolCountValue + 1;
                    }
                }

                xNudgeSymbol.showMultiplierOnXNudge(multiplier, multipliersYIdx[step]);

                // #226
                if (isWin) {
                    // #526
                    KennethMustDie.soundPlayer.playSound(KMDSound.X_NUDGE_NUDGE[step]);

                    // #298
                    KennethMustDie.winOverlays.playXNudgeMultiUpAnimation(reelId, multipliersYIdx[step] + 1, xNudgeSymbol.getY());
                }
            }, (wholeTlDurationTllNow + (step * eachStepDuration)));

            tl.add(() => {
                const sym: KMDSymbol = (KennethMustDie.reelView.reelModels[reelId].getStopSymbolAt((nudgeSteps - step - 1)).positionable as KMDSymbol);

                if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
                    sym.removeMultiplierOnXNudge();
                } else {
                    sym.visitedByNudge = true;
                }
            }, (wholeTlDurationTllNow + ((step + 1) * eachStepDuration)));
        }
    }

    protected onSkip(tl: gsap.core.Timeline): void {
        tl.totalProgress(1);
    }

    private getMainReelsAnimName(symId: number, multipliersNudgeSteps: number[], multipliers: number[], multipliersYIdx: number[]): string {
        let animName: string;

        if (symId === 1) {
            animName = X_NUDGE_ANIMATIONS.UP_1_STEP;
            multipliersNudgeSteps.push(multipliers[0]);
            multipliersYIdx.push(-1);
        } else {
            animName = X_NUDGE_ANIMATIONS.UP_2_STEP;

            for (let i: number = (symId - 1); i >= 0; i--) {
                multipliersNudgeSteps.push(multipliers[i]);

                multipliersYIdx.push(-1);
            }
        }

        return animName;
    }

    // #418
    private handleStickyWildsAndStickyXNudge(reelId: number, symId: number): void {
        for (let ctr: number = symId; ctr >= 0; ctr--) {
            if (KennethMustDie.stickyWilds.checkIfSticky(reelId, ctr)) {
                KennethMustDie.stickyWilds.unlockWild(reelId, ctr);
            } else if (ctr > 0 && KennethMustDie.stickyXNudges.checkIfSticky(reelId, ctr)) {
                KennethMustDie.stickyXNudges.unlockXNudge(reelId, ctr - 1);
            }
        }
    }
}